import { useEffect, useRef, useState } from 'react';
import type { EntryPatch, MeasurementMetric } from '@tally/lib/types';
import { saveEntry } from '../lib/api.js';

const FIELD_BY_METRIC: Record<MeasurementMetric, keyof EntryPatch> = {
  waist:     'waist_cm',
  chest:     'chest_cm',
  shoulders: 'shoulders_cm',
};

export function HeroMeasurement({
  metric,
  label,
  date,
  initial,
  onSaved,
}: {
  metric: MeasurementMetric;
  label: string;
  date: string;
  initial: number | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial != null ? String(initial) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(initial != null ? String(initial) : '');
  }, [initial]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === '' && initial == null) {
      setEditing(false);
      return;
    }
    const parsed = parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setErr('invalid');
      return;
    }
    if (parsed === initial) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await saveEntry({ date, [FIELD_BY_METRIC[metric]]: parsed } as EntryPatch);
      setEditing(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-l border-stone-700 pl-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-mono mb-1">{label}</div>
      {editing ? (
        <div>
          <div className="flex items-baseline gap-1">
            <input
              ref={inputRef}
              type="number"
              step="0.1"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commit();
                if (e.key === 'Escape') { setEditing(false); setErr(null); setDraft(initial != null ? String(initial) : ''); }
              }}
              onBlur={() => void commit()}
              disabled={saving}
              placeholder="—"
              className="w-24 bg-transparent text-5xl font-light tabular-nums text-stone-100 placeholder:text-stone-700 focus:outline-none"
            />
            <span className="text-sm text-stone-500 font-mono">cm</span>
          </div>
          <div className="text-[10px] text-stone-500 font-mono mt-1">
            {saving ? 'saving…' : err ?? 'enter ↵ · esc to cancel'}
          </div>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="text-left group">
          <div className="text-5xl font-light tabular-nums text-stone-100 group-hover:text-white">
            {initial != null ? initial : '—'}
            <span className="text-sm text-stone-500 font-mono ml-1">cm</span>
          </div>
          <div className="text-[10px] text-stone-500 font-mono mt-1 group-hover:text-stone-300">
            {initial != null ? 'tap to update' : 'tap to log'}
          </div>
        </button>
      )}
    </div>
  );
}
